import { useState, useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { RootState } from 'App'
import { getAuthToken } from 'lib/auth'
import { getTenantId } from 'utils/getTenantId'
import { useCoachTarget } from 'components/Common/AppFloatingChat/useCoachTarget.hook'
import {
  applyStreamComplete,
  parseStoredJsonArray,
} from 'components/Common/AppFloatingChat/streamComplete'
import { streamContextFields } from 'components/Common/AppFloatingChat/streamRequest'
import copyToClipboard from '../../../utils/copyToClipboard'
import { Message, StreamingResponse, ThinkingStep } from './types'
import { friendlyAgentLabel, friendlyToolLabel } from './thinking-labels'
import {
  useGetMessagesFromBotQuery,
  useCreateFlowChatSessionMutation,
  OnboardingFlowType,
} from '../../../__generated__/api-types-and-hooks'

interface InitialMessage {
  content: string
  role: 'assistant' | 'user'
}

// Storage key for pending requests
const PENDING_REQUESTS_KEY = 'insight-feedback-pending-requests'
const RESPONSE_TIMEOUT_MS = 5 * 60 * 1000 // 5 minutes
const POLL_INTERVAL_MS = 3000 // 3 seconds

interface PendingRequest {
  sessionId: string
  userMessage: string
  timestamp: number
}

// `code` is FORBIDDEN | SESSION_FORBIDDEN | INTERNAL on an 'error' line
type StreamLine = StreamingResponse & { code?: string }

/**
 * The steps still running when the answer is over end with it: the new coach never closes its
 * first 'thinking' step (only the old one sends 'planner_decision'), so it would spin for good.
 */
const finishRunningSteps = (trace: ThinkingStep[] | undefined, endedAt: number) =>
  trace?.map((step) =>
    step.status === 'running' ? { ...step, status: 'done' as const, endedAt } : step
  )

// What getMessagesFromBot answers for a thread the caller may not open (e.g. it is
// private to whoever started it). Any other failure may be temporary.
const SESSION_REFUSED_MESSAGE = 'You do not have access to this chat session'
const isSessionRefused = (error: unknown): boolean =>
  error instanceof Error && error.message.includes(SESSION_REFUSED_MESSAGE)

// This user's own thread about an item, by `${itemId}:${userId}`: kept on this device
// when the item already links a thread of someone else, or when an advisor talks about
// a client's item, so the item's link is never taken from whoever started its thread.
const OWN_THREADS_KEY = 'insight-feedback-own-threads'

function getOwnThread(key: string): string | null {
  try {
    const stored = JSON.parse(localStorage.getItem(OWN_THREADS_KEY) || '{}')
    return typeof stored?.[key] === 'string' ? stored[key] : null
  } catch {
    return null
  }
}

function saveOwnThread(key: string, sessionId: string) {
  try {
    const stored = JSON.parse(localStorage.getItem(OWN_THREADS_KEY) || '{}')
    localStorage.setItem(OWN_THREADS_KEY, JSON.stringify({ ...stored, [key]: sessionId }))
  } catch (e) {
    console.error('Failed to save own thread:', e)
  }
}

// In-memory cache for chat state (persists across dialog open/close)
interface ChatStateCache {
  messages: Message[]
  sessionId: string | null
  isTyping: boolean
  lastAccessed: number // For LRU cleanup
}
const chatStateCache: Record<string, ChatStateCache> = {}
const MAX_CACHE_ENTRIES = 20 // Limit cache size to prevent memory leaks

/**
 * Cleanup old cache entries if cache exceeds max size (LRU eviction)
 */
function cleanupOldCacheEntries() {
  const keys = Object.keys(chatStateCache)
  if (keys.length <= MAX_CACHE_ENTRIES) return

  // Sort by lastAccessed (oldest first) and remove excess entries
  const sortedKeys = keys.sort(
    (a, b) => (chatStateCache[a].lastAccessed || 0) - (chatStateCache[b].lastAccessed || 0)
  )
  const entriesToRemove = sortedKeys.slice(0, keys.length - MAX_CACHE_ENTRIES)
  entriesToRemove.forEach((key) => delete chatStateCache[key])
  console.log(`Cache cleanup: removed ${entriesToRemove.length} old entries`)
}

/**
 * Get pending requests from localStorage
 */
function getPendingRequests(): Record<string, PendingRequest> {
  try {
    const stored = localStorage.getItem(PENDING_REQUESTS_KEY)
    return stored ? JSON.parse(stored) : {}
  } catch {
    return {}
  }
}

/**
 * Save pending request to localStorage
 */
function savePendingRequest(sessionId: string, userMessage: string) {
  try {
    const pending = getPendingRequests()
    pending[sessionId] = { sessionId, userMessage, timestamp: Date.now() }
    localStorage.setItem(PENDING_REQUESTS_KEY, JSON.stringify(pending))
  } catch (e) {
    console.error('Failed to save pending request:', e)
  }
}

/**
 * Remove pending request from localStorage
 */
function removePendingRequest(sessionId: string) {
  try {
    const pending = getPendingRequests()
    delete pending[sessionId]
    localStorage.setItem(PENDING_REQUESTS_KEY, JSON.stringify(pending))
  } catch (e) {
    console.error('Failed to remove pending request:', e)
  }
}

/**
 * Parse timestamp from various formats (unix ms, unix seconds, ISO string)
 */
function parseTimestamp(timestamp: string | number): Date {
  if (typeof timestamp === 'number') {
    return timestamp > 10000000000 ? new Date(timestamp) : new Date(timestamp * 1000)
  }
  const numericTs = parseInt(timestamp, 10)
  if (!isNaN(numericTs)) {
    return numericTs > 10000000000 ? new Date(numericTs) : new Date(numericTs * 1000)
  }
  const date = new Date(timestamp)
  return isNaN(date.getTime()) ? new Date() : date
}

/**
 * Check if a message timestamp is recent (within timeout period)
 */
function isRecentMessage(timestamp: Date): boolean {
  return Date.now() - timestamp.getTime() < RESPONSE_TIMEOUT_MS
}

/**
 * Hook for managing Coach Logic streaming chat with backend persistence
 */
export function useCoachLogicChat(insightId?: string, initialSessionId?: string | null) {
  // Get cache key based on insightId
  const cacheKey = insightId || 'default'

  // Direct cache read function - always reads current cache
  const getCache = () => chatStateCache[cacheKey]
  const cachedState = getCache() // Read once per render

  // Initialize state from cache or defaults
  const [messages, setMessagesState] = useState<Message[]>(cachedState?.messages || [])
  const [sessionId, setSessionIdState] = useState<string | null>(cachedState?.sessionId || null)
  const [isTyping, setIsTypingState] = useState(cachedState?.isTyping || false)
  const [isLoadingHistory, setIsLoadingHistory] = useState(false)
  const [shouldFetchHistory, setShouldFetchHistory] = useState(false)
  const [requestFailed, setRequestFailed] = useState(false)

  // Keep a ref to current values for synchronous cache updates and access in callbacks
  const messagesRef = useRef<Message[]>(cachedState?.messages || [])
  const sessionIdRef = useRef<string | null>(cachedState?.sessionId || null)
  const isTypingRef = useRef<boolean>(cachedState?.isTyping || false)

  // Track the previous cacheKey to detect changes
  const prevCacheKeyRef = useRef<string>(cacheKey)

  // Use useLayoutEffect for synchronous state syncing when cacheKey changes
  // This handles the rare case where insightId changes without unmounting
  // (e.g., if the dialog is used for multiple insights in succession)
  useLayoutEffect(() => {
    // Skip on initial mount - useState initializers already have correct values
    if (prevCacheKeyRef.current !== cacheKey) {
      prevCacheKeyRef.current = cacheKey

      const cached = chatStateCache[cacheKey]

      // Update refs first (synchronous access for callbacks)
      messagesRef.current = cached?.messages || []
      sessionIdRef.current = cached?.sessionId || null
      isTypingRef.current = cached?.isTyping || false

      // Force state update to match cache
      setMessagesState(cached?.messages || [])
      setSessionIdState(cached?.sessionId || null)
      setIsTypingState(cached?.isTyping || false)
      setIsLoadingHistory(false)
      setShouldFetchHistory(false)
    }
  }, [cacheKey])

  // Wrapper functions that update cache synchronously
  const setMessages = useCallback(
    (updater: Message[] | ((prev: Message[]) => Message[])) => {
      const newMessages = typeof updater === 'function' ? updater(messagesRef.current) : updater
      // Update ref and cache synchronously
      messagesRef.current = newMessages
      chatStateCache[cacheKey] = {
        messages: newMessages,
        sessionId: sessionIdRef.current,
        isTyping: isTypingRef.current,
        lastAccessed: Date.now(),
      }
      // Cleanup old cache entries to prevent memory leaks
      cleanupOldCacheEntries()
      // Then update React state
      setMessagesState(newMessages)
    },
    [cacheKey]
  )

  const setSessionId = useCallback(
    (id: string | null) => {
      sessionIdRef.current = id
      chatStateCache[cacheKey] = {
        messages: messagesRef.current,
        sessionId: id,
        isTyping: isTypingRef.current,
        lastAccessed: Date.now(),
      }
      setSessionIdState(id)
    },
    [cacheKey]
  )

  const setIsTyping = useCallback(
    (typing: boolean) => {
      isTypingRef.current = typing
      chatStateCache[cacheKey] = {
        messages: messagesRef.current,
        sessionId: sessionIdRef.current,
        isTyping: typing,
        lastAccessed: Date.now(),
      }
      setIsTypingState(typing)
    },
    [cacheKey]
  )

  // Track initialization
  const initializedRef = useRef<string>('')

  // Track active streaming request for cleanup
  const abortControllerRef = useRef<AbortController | null>(null)

  // Track polling interval for cleanup
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const pollingErrorCountRef = useRef<number>(0) // Track consecutive polling errors
  const MAX_POLLING_ERRORS = 3 // Stop polling after this many consecutive errors

  // Advisor/admin on a client page: the conversation is about that client's business
  const { clientId } = useCoachTarget()
  // The current route, read when a message is sent (the send callback may be older).
  const { pathname } = useLocation()
  const pathnameRef = useRef(pathname)
  pathnameRef.current = pathname

  // A stored thread this user may not open (e.g. it is now private to whoever
  // started it). Dropped once the dialog has greeted the user.
  const unreadableSessionRef = useRef<string | null>(null)

  // The thread the item links (insight/play `session`): a new thread only becomes the
  // item's link when it had none. Otherwise it is kept as this user's own (see saveOwnThread).
  const itemSessionRef = useRef(initialSessionId)
  itemSessionRef.current = initialSessionId
  const userId = useSelector((state: RootState) => state.user.user?.id)
  const ownThreadKey = insightId && userId ? `${insightId}:${userId}` : null
  const ownThreadKeyRef = useRef(ownThreadKey)
  ownThreadKeyRef.current = ownThreadKey

  // Mutation to create a new session
  const { mutateAsync: createFlowChatSession } = useCreateFlowChatSessionMutation()

  // Fetch messages from backend
  const {
    data: messagesData,
    isLoading: isQueryLoading,
    isError: isHistoryError,
    error: historyError,
    refetch,
  } = useGetMessagesFromBotQuery(
    { sessionId: sessionId || '', limit: 100 },
    {
      enabled: !!sessionId && shouldFetchHistory,
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5000,
    }
  )

  // Cleanup polling
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current)
      pollingTimeoutRef.current = null
    }
    pollingErrorCountRef.current = 0 // Reset error count when stopping
  }, [])

  // Convert backend messages to local format
  const convertMessages = useCallback((backendMsgs: any[]): Message[] => {
    const converted = backendMsgs.map((msg, index) => ({
      id: `msg-${index}-${msg.createdAt}`,
      content: msg.content,
      sender: (msg.role === 'user' ? 'user' : 'coach') as 'user' | 'coach',
      timestamp: parseTimestamp(msg.createdAt),
      isVoiceMessage: msg.isVoiceMessage || false,
      voiceDuration: msg.voiceDuration || undefined,
      // Charts and diagrams saved with an answer
      reportBlocks: parseStoredJsonArray(msg.reportBlocks),
    }))
    // Sort by timestamp ascending (oldest first)
    converted.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())
    return converted
  }, [])

  // Start polling for response
  const startPolling = useCallback(
    (sid: string) => {
      // Clear any existing polling
      stopPolling()

      console.log('Starting polling for session:', sid)

      pollingIntervalRef.current = setInterval(async () => {
        try {
          const result = await refetch()
          const msgs = result.data?.getMessagesFromBot?.messages || []

          // Reset error count on successful fetch
          pollingErrorCountRef.current = 0

          if (msgs.length > 0) {
            const converted = convertMessages(msgs)
            setMessages(converted)

            const lastMsg = converted[converted.length - 1]
            // If last message is from coach, response is complete
            if (lastMsg.sender === 'coach') {
              console.log('Response received, stopping polling')
              removePendingRequest(sid)
              stopPolling()
              setIsTyping(false)
            }
          }
        } catch (error) {
          pollingErrorCountRef.current += 1
          console.error(
            `Polling error (${pollingErrorCountRef.current}/${MAX_POLLING_ERRORS}):`,
            error
          )

          // Stop polling after too many consecutive errors
          if (pollingErrorCountRef.current >= MAX_POLLING_ERRORS) {
            console.error('Too many polling errors, stopping polling')
            stopPolling()
            removePendingRequest(sid)
            setIsTyping(false)
            setRequestFailed(true)
            setMessages((prev) => [
              ...prev,
              {
                id: `error-network-${Date.now()}`,
                content: 'Unable to get response due to network issues. Please try again.',
                sender: 'coach',
                timestamp: new Date(),
              },
            ])
          }
        }
      }, POLL_INTERVAL_MS)

      // Set timeout to stop polling after 5 minutes
      pollingTimeoutRef.current = setTimeout(() => {
        console.log('Polling timeout reached')
        stopPolling()
        removePendingRequest(sid)
        setIsTyping(false)
        setRequestFailed(true)
        // Add error message
        setMessages((prev) => [
          ...prev,
          {
            id: `error-timeout-${Date.now()}`,
            content: 'The response is taking longer than expected. Please try again.',
            sender: 'coach',
            timestamp: new Date(),
          },
        ])
      }, RESPONSE_TIMEOUT_MS)
    },
    [refetch, convertMessages, stopPolling, setMessages, setIsTyping]
  )

  // Process query results
  useEffect(() => {
    if (!sessionId || !shouldFetchHistory) return

    if (messagesData?.getMessagesFromBot?.messages !== undefined) {
      const backendMsgs = messagesData.getMessagesFromBot.messages || []
      if (backendMsgs.length > 0) {
        const converted = convertMessages(backendMsgs)
        setMessages(converted)

        // Check if last message is from user and is recent - means response is pending
        const lastMsg = converted[converted.length - 1]
        if (lastMsg?.sender === 'user' && isRecentMessage(lastMsg.timestamp)) {
          console.log('Last message is from user and recent, showing loader and starting polling')
          setIsTyping(true)
          startPolling(sessionId)
        } else {
          // Response already received or message is old
          removePendingRequest(sessionId)
          setIsTyping(false)
        }
      } else {
        // Empty messages from backend - stream might have failed
        // Clear messages so FeedbackDialog can show greeting messages
        console.log('No messages from backend, clearing to show greeting messages')
        setMessages([])
        setIsTyping(false)
      }
      setShouldFetchHistory(false)
      setIsLoadingHistory(false)
    } else if (isHistoryError) {
      // Same as an empty history, so the dialog shows its greeting. A refused
      // thread is dropped below once that greeting is on screen; after any other
      // failure the thread is kept, so the next message still goes to it.
      if (isSessionRefused(historyError)) unreadableSessionRef.current = sessionId
      setMessages([])
      setIsTyping(false)
      setShouldFetchHistory(false)
      setIsLoadingHistory(false)
    } else if (!isQueryLoading && messagesData) {
      setShouldFetchHistory(false)
      setIsLoadingHistory(false)
    }
  }, [
    messagesData,
    isQueryLoading,
    isHistoryError,
    historyError,
    sessionId,
    shouldFetchHistory,
    convertMessages,
    startPolling,
    setMessages,
    setIsTyping,
  ])

  // Forget the unreadable thread so the next message starts a new one
  // (sendMessage keeps it as this user's own).
  useEffect(() => {
    if (!unreadableSessionRef.current || messages.length === 0) return
    if (unreadableSessionRef.current === sessionId) setSessionId(null)
    unreadableSessionRef.current = null
  }, [messages.length, sessionId, setSessionId])

  // Sync loading state
  useEffect(() => {
    if (shouldFetchHistory && isQueryLoading) {
      setIsLoadingHistory(true)
    }
  }, [isQueryLoading, shouldFetchHistory])

  // Cleanup on unmount - only stop polling, DON'T abort the request
  // The streaming request continues in background and updates the cache
  // When user reopens, they'll see the response from cache
  useEffect(() => {
    return () => {
      // NOTE: We intentionally do NOT abort the request here
      // This allows the streaming to continue and update the cache
      // even when the dialog is closed
      stopPolling()
    }
  }, [stopPolling])

  // Send message to Coach Logic streaming API (non-blocking)
  const sendMessage = useCallback(
    async function send(
      userMessage: string,
      currentSessionId: string | null,
      onSessionCreated?: (sessionId: string) => void,
      initialMessages?: InitialMessage[],
      systemPrompt?: string // Optional system prompt for context-aware conversations
    ): Promise<string | null> {
      // Set typing immediately for optimistic UI
      setIsTyping(true)
      setRequestFailed(false)

      // Abort any existing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }

      // Create new abort controller and store signal reference
      const abortController = new AbortController()
      abortControllerRef.current = abortController
      const { signal } = abortController

      let activeSessionId = currentSessionId

      try {
        const streamChatbotUrl = process.env.REACT_APP_STREAM_CHATBOT_URL || ''
        const authToken = await getAuthToken()

        if (!authToken) {
          throw new Error('Authentication token is required')
        }

        // Check if aborted before continuing
        if (signal.aborted) {
          return activeSessionId
        }

        // If no session exists, create a new one first via GraphQL mutation
        if (!activeSessionId) {
          const response = await createFlowChatSession({
            input: {
              flow: OnboardingFlowType.GeneralChat,
              ...(clientId && { businessId: clientId }),
            },
          })
          activeSessionId = response.createFlowChatSession.id
          setSessionId(activeSessionId)
          if (itemSessionRef.current || clientId) {
            // The item links a thread this user cannot open, or this is an advisor on a
            // client's item: the new thread is this user's own, not the item's link.
            if (ownThreadKeyRef.current) saveOwnThread(ownThreadKeyRef.current, activeSessionId)
          } else {
            onSessionCreated?.(activeSessionId)
          }
        }

        // Check if aborted before continuing
        if (signal.aborted) {
          return activeSessionId
        }

        // Save pending request for recovery if user navigates away
        savePendingRequest(activeSessionId, userMessage)

        const requestBody: Record<string, unknown> = {
          userMessage,
          sessionId: activeSessionId,
        }

        // Include initial messages if provided (for first message in new session)
        if (initialMessages && initialMessages.length > 0) {
          requestBody.initialMessages = initialMessages
        }

        // Include system prompt if provided (for context-aware feedback conversations)
        if (systemPrompt) {
          requestBody.systemPrompt = systemPrompt
        }

        if (clientId) {
          requestBody.targetBusinessId = clientId
        }

        // The asker's timezone and page, like every Ask CL request
        Object.assign(requestBody, streamContextFields(pathnameRef.current, getTenantId()))

        const response = await fetch(streamChatbotUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: authToken,
          },
          body: JSON.stringify(requestBody),
          signal,
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const reader = response.body?.getReader()
        if (!reader) {
          throw new Error('No response body reader available')
        }

        const botMessageId = `bot-${Date.now()}`
        const decoder = new TextDecoder()
        let buffer = ''
        let fullContent = ''
        let messageAdded = false
        // The stored thread was refused (e.g. it is now private to whoever started it)
        let sessionForbidden = false

        const ensureBotMessage = () => {
          if (messageAdded) return
          const botMessage: Message = {
            id: botMessageId,
            sender: 'coach',
            content: '',
            timestamp: new Date(),
            isThinking: true,
            thinkingTrace: [],
            reasoningText: '',
          }
          setMessages((prev) => [...prev, botMessage])
          messageAdded = true
        }

        const updateBotMessage = (updater: (m: Message) => Message) => {
          setMessages((prev) => prev.map((msg) => (msg.id === botMessageId ? updater(msg) : msg)))
        }

        const findLastRunningStep = (
          trace: ThinkingStep[],
          predicate: (s: ThinkingStep) => boolean
        ): number => {
          for (let i = trace.length - 1; i >= 0; i -= 1) {
            if (trace[i].status === 'running' && predicate(trace[i])) return i
          }
          return -1
        }

        const handleStatus = (data: StreamingResponse) => {
          ensureBotMessage()
          const now = Date.now()
          updateBotMessage((m) => {
            const trace = m.thinkingTrace ? [...m.thinkingTrace] : []
            let reasoningText = m.reasoningText || ''
            let checklist = m.checklist

            switch (data.status) {
              case 'thinking':
              case 'executing': {
                if (!trace.some((s) => s.kind === 'planner' && s.status === 'running')) {
                  trace.push({
                    id: `planner-${now}`,
                    kind: 'planner',
                    label: data.message || 'Thinking',
                    status: 'running',
                    startedAt: now,
                  })
                }
                break
              }
              case 'planner_decision': {
                const idx = findLastRunningStep(trace, (s) => s.kind === 'planner')
                if (idx >= 0) {
                  trace[idx] = { ...trace[idx], status: 'done', endedAt: now }
                }
                if (data.reasoning) {
                  reasoningText = reasoningText
                    ? `${reasoningText}\n\n${data.reasoning}`
                    : data.reasoning
                }
                break
              }
              case 'agent_running': {
                trace.push({
                  id: `agent-${data.agent || 'unknown'}-${now}`,
                  kind: 'agent',
                  agent: data.agent,
                  label: data.message || friendlyAgentLabel(data.agent),
                  status: 'running',
                  parallel: data.parallel,
                  startedAt: now,
                })
                break
              }
              case 'agent_complete': {
                const idx = findLastRunningStep(
                  trace,
                  (s) => s.kind === 'agent' && s.agent === data.agent
                )
                if (idx >= 0) {
                  trace[idx] = { ...trace[idx], status: 'done', endedAt: now }
                }
                break
              }
              case 'tool_start': {
                trace.push({
                  id: `tool-${data.tool || 'unknown'}-${now}`,
                  kind: 'tool',
                  agent: data.agent,
                  tool: data.tool,
                  label: data.message || friendlyToolLabel(data.tool),
                  status: 'running',
                  inputPreview: data.inputPreview,
                  startedAt: now,
                })
                break
              }
              case 'tool_end': {
                const idx = findLastRunningStep(
                  trace,
                  (s) => s.kind === 'tool' && s.tool === data.tool
                )
                if (idx >= 0) {
                  trace[idx] = {
                    ...trace[idx],
                    status: 'done',
                    endedAt: now,
                    resultPreview: data.resultPreview,
                  }
                }
                break
              }
              case 'reasoning_token': {
                if (data.delta) reasoningText += data.delta
                break
              }
              case 'checklist_update': {
                // Backend sends the FULL list each update; replace verbatim.
                if (data.checklist) checklist = data.checklist
                break
              }
              default:
                break
            }

            return { ...m, thinkingTrace: trace, checklist, reasoningText, isThinking: true }
          })
        }

        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() || ''

          for (const line of lines) {
            if (line.trim()) {
              try {
                const data: StreamLine = JSON.parse(line)

                if (data.type === 'session') {
                  continue
                } else if (data.type === 'status') {
                  handleStatus(data)
                } else if (data.type === 'content') {
                  fullContent += data.content || ''
                  const currentContent = fullContent
                  ensureBotMessage()
                  updateBotMessage((m) => ({ ...m, content: currentContent }))
                } else if (data.type === 'complete') {
                  const streamedText = fullContent
                  ensureBotMessage()
                  // The final text with the answer's charts, diagrams and sources
                  setMessages((prev) =>
                    applyStreamComplete(prev, botMessageId, data, { text: streamedText }).map(
                      (msg) =>
                        msg.id === botMessageId
                          ? {
                              ...msg,
                              isThinking: false,
                              thinkingTrace: finishRunningSteps(msg.thinkingTrace, Date.now()),
                            }
                          : msg
                    )
                  )
                  removePendingRequest(activeSessionId!)
                } else if (
                  data.type === 'error' &&
                  data.code === 'SESSION_FORBIDDEN' &&
                  currentSessionId
                ) {
                  sessionForbidden = true
                } else if (data.type === 'error') {
                  const errorContent =
                    data.error || 'Sorry, I encountered an error. Please try again.'
                  ensureBotMessage()
                  updateBotMessage((m) => {
                    const trace = m.thinkingTrace ? [...m.thinkingTrace] : []
                    for (let i = trace.length - 1; i >= 0; i -= 1) {
                      if (trace[i].status === 'running') {
                        trace[i] = {
                          ...trace[i],
                          status: 'failed',
                          endedAt: Date.now(),
                          error: errorContent,
                        }
                      }
                    }
                    return {
                      ...m,
                      content: m.content || errorContent,
                      thinkingTrace: trace,
                      isThinking: false,
                    }
                  })
                  removePendingRequest(activeSessionId!)
                  setRequestFailed(true)
                }
              } catch (e) {
                console.error('Error parsing streaming response:', e)
              }
            }
          }
        }

        // Also when the stream closed without a 'complete' line
        if (messageAdded) {
          updateBotMessage((m) => ({
            ...m,
            thinkingTrace: finishRunningSteps(m.thinkingTrace, Date.now()),
          }))
        }
        removePendingRequest(activeSessionId!)
        if (sessionForbidden) {
          // Send it again in a new thread (kept as this user's own, see above).
          if (abortControllerRef.current === abortController) {
            abortControllerRef.current = null
          }
          setSessionId(null)
          return send(userMessage, null, onSessionCreated, initialMessages, systemPrompt)
        }
        setIsTyping(false)
        // Only clear ref if this is still the current controller
        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null
        }
        return activeSessionId
      } catch (error: any) {
        // Don't show error if request was aborted (user navigated away)
        if (error.name === 'AbortError') {
          console.log('Request aborted - user navigated away')
          // Keep pending request and typing state so polling can pick it up when user returns
          return activeSessionId
        }

        console.error('Error sending message to Coach Logic:', error)
        setMessages((prev) => [
          ...prev,
          {
            id: `error-${Date.now()}`,
            sender: 'coach',
            content: 'Sorry, I encountered an error. Please try again.',
            timestamp: new Date(),
          },
        ])

        if (activeSessionId) {
          removePendingRequest(activeSessionId)
        }
        setRequestFailed(true)
        setIsTyping(false)
        // Only clear ref if this is still the current controller
        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null
        }
        return currentSessionId
      }
    },
    [createFlowChatSession, setIsTyping, setMessages, setSessionId, clientId]
  )

  // Add a user message to local state
  const addUserMessage = useCallback(
    (content: string, options?: Partial<Message>) => {
      const userMessage: Message = {
        id: `user-${Date.now()}`,
        content,
        sender: 'user',
        timestamp: new Date(),
        ...options,
      }
      setMessages((prev) => [...prev, userMessage])
      return userMessage
    },
    [setMessages]
  )

  // Initialize - set session and trigger loading if needed
  const initialize = useCallback(
    (itemSession?: string | null) => {
      // This user's own thread about the item comes first (see saveOwnThread).
      const existingSession = (ownThreadKey && getOwnThread(ownThreadKey)) || itemSession
      const initKey = `${insightId}-${existingSession || 'new'}`

      // Check if we have cached state FIRST (user closed dialog while waiting for response)
      const cached = chatStateCache[cacheKey]
      if (cached?.messages?.length > 0) {
        // Check if last message is from coach - if so, response is complete, isTyping should be false
        const lastMessage = cached.messages[cached.messages.length - 1]
        const isResponseComplete = lastMessage?.sender === 'coach'
        const shouldBeTyping = cached.isTyping && !isResponseComplete

        // Always restore from cache if available - update refs and state
        messagesRef.current = cached.messages
        sessionIdRef.current = cached.sessionId
        isTypingRef.current = shouldBeTyping
        setMessagesState(cached.messages)
        setSessionIdState(cached.sessionId)
        setIsTypingState(shouldBeTyping)
        setShouldFetchHistory(false)
        setIsLoadingHistory(false)
        setRequestFailed(false)
        initializedRef.current = initKey

        // Update cache with corrected isTyping state
        if (cached.isTyping !== shouldBeTyping) {
          chatStateCache[cacheKey] = {
            ...cached,
            isTyping: shouldBeTyping,
            lastAccessed: Date.now(),
          }
        }

        // If still typing, start polling to get the response
        if (shouldBeTyping && cached.sessionId) {
          startPolling(cached.sessionId)
        }

        return { hasExisting: true }
      }

      // Prevent re-initialization with same values (only if no cache)
      if (initializedRef.current === initKey) {
        return { hasExisting: !!existingSession }
      }

      initializedRef.current = initKey
      setRequestFailed(false)

      if (existingSession) {
        setSessionId(existingSession)
        setShouldFetchHistory(true)
        setIsLoadingHistory(true)
        return { hasExisting: true }
      }

      // No existing session - start fresh
      setMessages([])
      setSessionId(null)
      setShouldFetchHistory(false)
      setIsLoadingHistory(false)
      return { hasExisting: false }
    },
    [insightId, cacheKey, ownThreadKey, startPolling, setMessages, setSessionId]
  )

  // Reset the conversation
  const reset = useCallback(() => {
    setMessages([])
    setSessionId(null)
    setIsTyping(false)
    setIsLoadingHistory(false)
    setShouldFetchHistory(false)
    setRequestFailed(false)
    initializedRef.current = ''
    stopPolling()
    // Clear cache for this insight
    delete chatStateCache[cacheKey]
  }, [stopPolling, setMessages, setSessionId, setIsTyping, cacheKey])

  // Check if user has sent a message
  const hasUserMessage = messages.some((m) => m.sender === 'user')

  return {
    messages,
    setMessages,
    sessionId,
    setSessionId,
    isTyping,
    isLoadingHistory,
    requestFailed,
    sendMessage,
    addUserMessage,
    initialize,
    reset,
    hasUserMessage,
  }
}

/**
 * Hook for text-to-speech functionality
 */
export function useSpeechSynthesis() {
  const [isSpeaking, setIsSpeaking] = useState(false)
  const isMountedRef = useRef(true)

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      if ('speechSynthesis' in window) {
        speechSynthesis.cancel()
      }
    }
  }, [])

  const speak = useCallback(
    (text: string) => {
      try {
        if (!('speechSynthesis' in window)) {
          console.warn('Speech synthesis not supported')
          return
        }

        if (isSpeaking) {
          speechSynthesis.cancel()
          if (isMountedRef.current) setIsSpeaking(false)
          return
        }

        speechSynthesis.cancel()

        const speakText = () => {
          const utterance = new SpeechSynthesisUtterance(text)
          utterance.rate = 0.9
          utterance.pitch = 1.0
          utterance.volume = 1.0

          const voices = speechSynthesis.getVoices()
          const preferredVoice =
            voices.find(
              (voice) =>
                voice.lang.startsWith('en') &&
                (voice.name.includes('Female') || voice.name.includes('Google'))
            ) ||
            voices.find((voice) => voice.lang.startsWith('en')) ||
            voices[0]

          if (preferredVoice) utterance.voice = preferredVoice

          utterance.onstart = () => {
            if (isMountedRef.current) setIsSpeaking(true)
          }
          utterance.onend = () => {
            if (isMountedRef.current) setIsSpeaking(false)
          }
          utterance.onerror = () => {
            if (isMountedRef.current) setIsSpeaking(false)
          }

          speechSynthesis.speak(utterance)
        }

        const voices = speechSynthesis.getVoices()
        if (voices.length === 0) {
          speechSynthesis.addEventListener('voiceschanged', speakText, { once: true })
        } else {
          speakText()
        }
      } catch (error) {
        console.error('Failed to speak:', error)
        if (isMountedRef.current) setIsSpeaking(false)
      }
    },
    [isSpeaking]
  )

  const cancel = useCallback(() => {
    if ('speechSynthesis' in window) {
      speechSynthesis.cancel()
    }
    if (isMountedRef.current) setIsSpeaking(false)
  }, [])

  return { isSpeaking, speak, cancel }
}

/**
 * Hook for clipboard operations
 */
export function useClipboard() {
  const [copiedId, setCopiedId] = useState<string | null>(null)

  const copy = useCallback(async (text: string, id: string) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        copyToClipboard(text)
        setCopiedId(id)
        setTimeout(() => setCopiedId(null), 2000)
        return
      }

      // Fallback for older browsers / non-secure context
      const textArea = document.createElement('textarea')
      textArea.value = text
      textArea.style.position = 'fixed'
      textArea.style.left = '-999999px'
      textArea.style.top = '-999999px'
      textArea.setAttribute('readonly', '')
      document.body.appendChild(textArea)

      textArea.select()
      textArea.setSelectionRange(0, 99999)

      document.execCommand('copy')
      textArea.remove()

      setCopiedId(id)
      setTimeout(() => setCopiedId(null), 2000)
    } catch (error) {
      console.error('Failed to copy text:', error)
      alert('Failed to copy to clipboard. Please select the text and copy manually.')
    }
  }, [])

  return { copiedId, copy }
}
